import { Controller } from "@hotwired/stimulus";
import createGlobe from "cobe";

export default class extends Controller {
  static targets = ["canvas"];

  connect() {
    let phi = 0;
    let theta = 0.2;
    let dragTargetX = 0;
    let dragTargetY = 0;
    let dragCurrentX = 0;
    let dragCurrentY = 0;
    this.pointerX = null;
    this.pointerY = null;

    this.markers = [
      { id: "sf", location: [37.78, -122.44], label: "San Francisco" },
      { id: "nyc", location: [40.71, -74.01], label: "New York" },
      { id: "london", location: [51.5, -0.12], label: "London" },
      { id: "dubai", location: [25.2, 55.27], label: "Dubai" },
      { id: "amman", location: [31.95, 35.93], label: "Amman" },
      { id: "tokyo", location: [35.67, 139.65], label: "Tokyo" },
      { id: "dhaka", location: [23.81, 90.41], label: "Dhaka" },
      { id: "saopaulo", location: [-23.55, -46.63], label: "São Paulo" },
      { id: "miami", location: [25.76, -80.19], label: "Miami" },
    ];

    const size = this.canvasTarget.offsetWidth;
    this.canvasTarget.width = size * 2;
    this.canvasTarget.height = size * 2;

    this.globe = createGlobe(this.canvasTarget, {
      devicePixelRatio: 2,
      width: size * 2,
      height: size * 2,
      phi: 0,
      theta: 0.2,
      dark: 0,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [1, 1, 1],
      markerColor: [0, 0.427, 0.184],
      glowColor: [1, 1, 1],
      markers: this.markers.map((m) => ({
        location: m.location,
        size: 0.03,
        id: m.id,
      })),
      arcs: [
        { from: [37.78, -122.44], to: [40.71, -74.01] },
        { from: [51.5, -0.12], to: [25.2, 55.27] },
        { from: [35.67, 139.65], to: [23.81, 90.41] },
        { from: [-23.55, -46.63], to: [25.76, -80.19] },
        { from: [23.81, 90.41], to: [31.95, 35.93] },
      ],
      arcColor: [0.102, 0.671, 0.322],
      arcWidth: 0.5,
      arcHeight: 0.3,
    });

    const animate = () => {
      dragCurrentX += (dragTargetX - dragCurrentX) * 0.15;
      dragCurrentY += (dragTargetY - dragCurrentY) * 0.15;
      if (this.pointerX === null) phi += 0.003;
      this.globe.update({
        phi: phi + dragCurrentX,
        theta: theta + dragCurrentY,
      });
      this.rafId = requestAnimationFrame(animate);
    };
    animate();

    this._setDragTargetX = (v) => (dragTargetX = v);
    this._setDragTargetY = (v) => (dragTargetY = v);
    this._getDragCurrentX = () => dragCurrentX;
    this._getDragCurrentY = () => dragCurrentY;
    this._addToPhi = (v) => (phi += v);
    this._addToTheta = (v) => (theta += v);
    this._resetDrag = () => {
      dragTargetX = 0;
      dragCurrentX = 0;
      dragTargetY = 0;
      dragCurrentY = 0;
    };

    this._bindDrag();

    this.markers.forEach((m) => {
      const el = document.createElement("div");
      el.className = "marker-label";
      el.textContent = m.label;
      el.style.positionAnchor = `--cobe-${m.id}`;
      el.style.opacity = `var(--cobe-visible-${m.id}, 0)`;
      this.canvasTarget.parentElement.appendChild(el);
    });
  }

  disconnect() {
    cancelAnimationFrame(this.rafId);
    this.globe?.destroy();
    this._unbindDrag?.();
  }

  _bindDrag() {
    const c = this.canvasTarget;
    c.style.cursor = "grab";
    this._onDown = this._onPointerDown.bind(this);
    this._onMove = this._onPointerMove.bind(this);
    this._onUp = this._onPointerUp.bind(this);
    c.addEventListener("pointerdown", this._onDown);
    c.addEventListener("pointermove", this._onMove);
    c.addEventListener("pointerup", this._onUp);
    c.addEventListener("pointerleave", this._onUp);
  }

  _unbindDrag() {
    const c = this.canvasTarget;
    c.removeEventListener("pointerdown", this._onDown);
    c.removeEventListener("pointermove", this._onMove);
    c.removeEventListener("pointerup", this._onUp);
    c.removeEventListener("pointerleave", this._onUp);
  }

  _onPointerDown(e) {
    this.pointerX = e.clientX;
    this.pointerY = e.clientY;
    this.canvasTarget.style.cursor = "grabbing";
    this.canvasTarget.setPointerCapture(e.pointerId);
  }

  _onPointerMove(e) {
    if (this.pointerX === null) return;
    const dx = e.clientX - this.pointerX;
    const dy = e.clientY - this.pointerY;
    this._setDragTargetX(dx / 200);
    this._setDragTargetY(Math.max(-0.5, Math.min(0.5, dy / 200)));
  }

  _onPointerUp() {
    this.pointerX = null;
    this.pointerY = null;
    this.canvasTarget.style.cursor = "grab";
    this._addToPhi(this._getDragCurrentX());
    this._addToTheta(this._getDragCurrentY());
    this._resetDrag();
  }
}
